// src/observer/reveal.ts (GSAP-driven reveals; no gsapSection needed)
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { animateElements } from "../elements/animate";

// Keep in sync with animateElements' target list (only used for initial priming)
export const ANIM_EL_SELECTOR =
  'h1, h2, h3, p, .lead, .section-image, hr, span[data-anim-delay], [data-anim], [data-animated]';

// Elements inside these containers should never be primed/hidden
const EXCLUDE_ANCESTOR =
  ".hud-reel-controls, .lite-hud-controls, .reel-audio-gate, .pause-overlay, .sc-captions-layer";

gsap.registerPlugin(ScrollTrigger);
ScrollTrigger.defaults({ scroller: "#scroll-wrapper" });

type SetupOpts = {
  /** id or element of the scroll container; default '#scroll-wrapper' */
  scroller?: string | HTMLElement;
  /** Where in the viewport reveals should start; default 'top 85%' */
  start?: string;
  /** If true, skip reveals when user prefers-reduced-motion (default: true) */
  honorReducedMotion?: boolean;
  /** If true, wait for audio gate/unlock before animating (default: false) */
  requireAudioAllowed?: boolean;
};

/** Light priming to avoid flash of revealed content before GSAP runs. */
export function primeSectionForReveal(section: HTMLElement) {
  if (section.dataset.revealPrimed === "1") return;
  section.dataset.revealPrimed = "1";
  section.querySelectorAll<HTMLElement>(ANIM_EL_SELECTOR).forEach((el) => {
    // Skip anything inside HUD/captions/audio gate overlays
    if (el.closest(EXCLUDE_ANCESTOR)) return;
    if (el.dataset.animated !== "true") el.dataset.animated = "false";
  });
}

function prefersReducedMotion(): boolean {
  try { return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false; }
  catch { return false; }
}

function audioAllowed(): boolean {
  const gateFlag = document.body?.dataset?.audioAllowed === "1";
  const sectionGate = !!document.querySelector('section[data-audio-started="1"]');
  const audioTagGate = !!document.querySelector('audio[data-audio-started="1"]');
  const unlocked = !!(window as any).__storycore_audio?.enabled; // set by MediaUnlock if used
  return gateFlag || sectionGate || audioTagGate || unlocked;
}

/**
 * Create a ScrollTrigger for a single section.
 * Skips section-0 unless `data-allow-first-reveal="1"`.
 */
export function setupGsapRevealFor(
  section: HTMLElement,
  opts: SetupOpts = {}
) {
  const {
    honorReducedMotion = true,
    requireAudioAllowed = false,
  } = opts;

  const scrollerEl =
    typeof opts.scroller === "string"
      ? (document.querySelector(opts.scroller) as HTMLElement | null)
      : (opts.scroller as HTMLElement | null) ||
        (document.getElementById("scroll-wrapper") as HTMLElement | null);

  // Honor "skip first" rule (renderFirstSection can still do its own thing)
  if (section.dataset.sectionIndex === "0" && section.dataset.allowFirstReveal !== "1") {
    return;
  }

  // Avoid duplicating triggers if re-run
  if (section.dataset.revealStarted === "1") return;

  primeSectionForReveal(section);

  ScrollTrigger.create({
    trigger: section,
    scroller: scrollerEl || undefined, // undefined means default (window)
    start: opts.start || "top 85%",
    once: true, // fire a single time
    onEnter: () => {
      if (!document.body.contains(section)) return;
      section.dataset.revealStarted = "1";

      // A11y: if user prefers reduced motion, make elements visible without tweening
      if (honorReducedMotion && prefersReducedMotion()) {
        section.querySelectorAll<HTMLElement>('[data-anim], [data-animated]').forEach((el) => {
          if (el.closest(EXCLUDE_ANCESTOR)) return;
          el.style.opacity = "1";
          el.style.transform = "none";
          el.style.visibility = "visible";
          el.dataset.animated = "true";
        });
        section.classList.remove("is-primed");
        return;
      }

      // Optional: wait for audio gate/unlock if required
      if (requireAudioAllowed && !audioAllowed()) {
        // Defer the animation until audio is allowed; listen once
        const onAllow = () => {
          document.removeEventListener("sc:audio-allowed", onAllow as any);
          try { animateElements(section); } catch (e) { console.warn("[GSAP-REVEAL] animateElements failed:", e); }
          section.classList.remove("is-primed");
        };
        document.addEventListener("sc:audio-allowed", onAllow as any, { once: true });
        return;
      }

      // Run the GSAP tweens now
      try {
        animateElements(section);
      } catch (e) {
        console.warn("[GSAP-REVEAL] animateElements failed:", e);
      } finally {
        section.classList.remove("is-primed");
      }
    },
  });
}

/**
 * Create ScrollTriggers for all current sections.
 * Call this once after sections are in the DOM (e.g., after content load/render).
 */
export function setupGsapReveals(opts: SetupOpts = {}) {
  const sections = document.querySelectorAll<HTMLElement>("section[data-section-index]");
  sections.forEach((s) => setupGsapRevealFor(s, opts));

  // If your layout size depends on images/fonts, refresh ScrollTrigger after they’re ready.
  window.addEventListener("load", () => ScrollTrigger.refresh());
  // If you add sections dynamically later, you can call setupGsapRevealFor(newSection).
}